import { useCallback, useState } from 'react';

import { backupSettingsClient, levelValue } from '../api';
import { useBackupChoice, useBackupSave, useJobBackupSetting } from '../hooks';

import BackupsSetting from './BackupsSetting';
import { backupSavedToast } from './toast';

import SurgeToast, {
  SurgeToastMessage,
} from '@/routes/Agency/PaySurge/components/SurgeToast';

type Props = {
  jobId: number | string;
  /** Tenant admins only; everyone else gets nothing (and no request). */
  canManage: boolean;
};

/**
 * The job's auto backups setting on the Job page, under the surge pay
 * banner: the other automation that acts on a short shift by itself.
 */
const JobBackupsSetting = ({ jobId, canManage }: Props) => {
  const { data } = useJobBackupSetting(jobId, canManage);
  const [toast, setToast] = useState<SurgeToastMessage | null>(null);
  const dismiss = useCallback(() => setToast(null), []);

  const { save, saving, error } = useBackupSave(
    (enabled) => backupSettingsClient.setJob(jobId, enabled),
    'Could not save auto backups for this job. Please try again.',
    (saved, enabled) =>
      setToast(
        backupSavedToast(
          'job',
          enabled,
          saved.enabled ?? saved.order_enabled ?? saved.client_enabled,
        ),
      ),
  );
  const chooser = useBackupChoice(save);

  if (!canManage || !data) return null;

  return (
    <div
      className="dashv2-scope dashv2-bkpset-panel"
      data-testid="job-backups-section"
    >
      <BackupsSetting
        level="job"
        label="Auto backups for this job"
        {...levelValue('job', {
          job: data.enabled,
          order: data.order_enabled,
          client: data.client_enabled,
        })}
        counts={{
          onStandby: data.on_standby_count,
          invited: data.invited_count,
        }}
        switchOn={data.switch_on}
        saving={saving}
        error={error}
        chooser={chooser}
        testId="job-backups"
      />
      <SurgeToast toast={toast} onDismiss={dismiss} />
    </div>
  );
};

export default JobBackupsSetting;
